import {
	QueryClient,
	useInfiniteQuery,
	useMutation,
	UseMutationResult,
	useQuery,
} from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import {
	PAYMENT_API_MUTATION__CREATE_ERROR_TOAST,
	PAYMENT_API_MUTATION__CREATE_SUCCESS_TOAST,
	PAYMENT_API_MUTATION__STATUS_UPDATE_SUCCESS_TOAST,
	PAYMENT_API_MUTATION__UPDATE_ERROR_TOAST,
	PAYMENT_API_MUTATION__UPDATE_STATUS_ERROR_TOAST,
} from '../components/payments/data/constants';
import { errorToast, successToast } from '../components/Toast';
import {
	CreatePaymentUpdateRequest,
	Payment,
	PaymentActionRequest,
	PaymentCreateRequest,
	PaymentResponse,
	PaymentStatus,
} from '../types/payments';
import { getApiUrl } from '../util/util';
import { get, patch, post } from './http';

const PaymentsApi = {
	getPayments(
		authorizationToken: string,
		pagingToken?: string,
		pageSize?: number,
		policyholderLocator?: string,
		policyLocator?: string,
		state?: string,
	) {
		const params = new URLSearchParams();
		if (pagingToken) {
			params.append('pagingToken', pagingToken);
		}
		if (pageSize && !pagingToken) {
			params.append('pageSize', pageSize.toString());
		}

		if (policyholderLocator && !pagingToken) {
			params.append('policyholderLocator', policyholderLocator);
		}

		if (policyLocator && !pagingToken) {
			params.append('policyLocator', policyLocator);
		}

		if (state && !pagingToken) {
			params.append('state', state);
		}

		const url = `${getApiUrl()}/payments?${params.toString()}`;
		return get<PaymentResponse>(url, authorizationToken);
	},
	createPayment(authorizationToken: string, payment: PaymentCreateRequest) {
		return post<Payment>(
			`${getApiUrl()}/payments`,
			authorizationToken,
			payment,
		);
	},
	updatePayment(
		authorizationToken: string,
		paymentLocator: string,
		payment: PaymentActionRequest,
	) {
		return patch<PaymentActionRequest>(
			`${getApiUrl()}/payments/${paymentLocator}`,
			authorizationToken,
			payment,
		);
	},
	getPayment(authorizationToken: string, paymentLocator: string) {
		return get<Payment>(
			`${getApiUrl()}/payments/${paymentLocator}`,
			authorizationToken,
		);
	},
};

export const getPaymentsQueryKey = (
	authToken: string,
	policyholderLocator: string,
	policyLocator: string,
) => ['payments', authToken, policyholderLocator, policyLocator];

export const usePayments = (
	authToken: string,
	cashManagementEnabled = false,
	hasPermission: boolean,
	policyholderLocator?: string,
	policyLocator?: string,
) =>
	useQuery({
		queryKey: getPaymentsQueryKey(
			authToken,
			policyholderLocator,
			policyLocator,
		),
		queryFn: async () => {
			const response = await PaymentsApi.getPayments(
				authToken,
				null,
				null,
				policyholderLocator,
				policyLocator,
			);
			return response.data.payments;
		},
		enabled: cashManagementEnabled && hasPermission,
	});

export const useCreatePaymentMutation = ({
	readyToPost,
	authToken,
	updatePayment,
	queryClient,
}: {
	readyToPost: boolean;
	authToken: string;
	updatePayment: UseMutationResult<
		AxiosResponse<PaymentActionRequest, any>,
		unknown,
		CreatePaymentUpdateRequest,
		unknown
	>;
	queryClient: QueryClient;
}) =>
	useMutation({
		mutationFn: (payment: PaymentCreateRequest) =>
			PaymentsApi.createPayment(authToken, payment),
		onSuccess: async (newPayment, createRequest) => {
			if (readyToPost) {
				const locator = newPayment.data.locator;
				await updatePayment.mutateAsync({
					locator,
					request: {
						action: 'ready',
					},
				});
			} else {
				successToast(PAYMENT_API_MUTATION__CREATE_SUCCESS_TOAST);
				queryClient.invalidateQueries([
					'payments',
					newPayment.data.locator,
					authToken,
				]);
			}

			queryClient.invalidateQueries(
				getPaymentsQueryKey(
					authToken,
					newPayment?.data?.policyholderLocator,
					newPayment?.data?.policyLocator,
				),
			);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					PAYMENT_API_MUTATION__CREATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useUpdatePaymentAfterCreationMutation = ({
	authToken,
	queryClient,
}: {
	authToken: string;
	queryClient: QueryClient;
}) =>
	useMutation({
		mutationFn: (payment: CreatePaymentUpdateRequest) =>
			PaymentsApi.updatePayment(authToken, payment.locator, payment.request),
		onSuccess: (newPayment, createRequest) => {
			queryClient.invalidateQueries([
				'payments',
				createRequest.locator,
				authToken,
			]);
			successToast(PAYMENT_API_MUTATION__CREATE_SUCCESS_TOAST);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					PAYMENT_API_MUTATION__UPDATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useUpdatePaymentMutation = ({
	paymentLocator,
	authToken,
	queryClient,
}: {
	paymentLocator: string;
	authToken: string;
	queryClient: QueryClient;
}) =>
	useMutation({
		onMutate: async (updatedPayment) => {
			// Do an optimistic update to the cache so navigating back to the payment page is snappy and the values reflect the new state
			await queryClient.cancelQueries({
				queryKey: ['payments', paymentLocator, authToken],
			});

			const previousPayment = queryClient.getQueryData<Payment>([
				'payments',
				paymentLocator,
				authToken,
			]);

			const newPayment: Payment = {
				...previousPayment,
				...updatedPayment?.paymentUpdate,
				fieldValues: {
					...previousPayment?.fieldValues,
					...updatedPayment?.paymentUpdate?.fieldValues,
				},
			};

			queryClient.setQueryData(
				['payments', paymentLocator, authToken],
				newPayment,
			);

			return { previousPayment, updatedPayment };
		},
		mutationFn: (payment: PaymentActionRequest) =>
			PaymentsApi.updatePayment(authToken, paymentLocator, payment),
		onSuccess: (response, request) => {
			queryClient.invalidateQueries(['payments', paymentLocator, authToken]);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					PAYMENT_API_MUTATION__UPDATE_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const useUpdatePaymentStatusMutation = ({
	paymentLocator,
	authToken,
	queryClient,
}: {
	paymentLocator: string;
	authToken: string;
	queryClient: QueryClient;
}) =>
	useMutation({
		mutationFn: async (payment: PaymentActionRequest) => {
			return PaymentsApi.updatePayment(authToken, paymentLocator, payment);
		},
		onSuccess: () => {
			successToast(PAYMENT_API_MUTATION__STATUS_UPDATE_SUCCESS_TOAST);
			queryClient.invalidateQueries(['payments', paymentLocator, authToken]);
		},
		onError: (error) => {
			if (axios.isAxiosError(error)) {
				errorToast(
					PAYMENT_API_MUTATION__UPDATE_STATUS_ERROR_TOAST +
						error.response?.data.message,
				);
			}
		},
	});

export const getPaginatedPaymentsQueryKey = (
	pageSize: number,
	token: string,
	policyholderLocator?: string,
	policyLocator?: string,
	state?: PaymentStatus,
) => ['payments', pageSize, token, policyholderLocator, policyLocator, state];

export const usePaginatedPayments = (
	pageSize: number,
	token: string,
	policyholderLocator?: string,
	policyLocator?: string,
	state?: PaymentStatus,
) => {
	return useInfiniteQuery(
		getPaginatedPaymentsQueryKey(
			pageSize,
			token,
			policyholderLocator,
			policyLocator,
			state,
		),
		async ({ pageParam = 0 }) => {
			const response = await PaymentsApi.getPayments(
				token,
				pageParam,
				pageSize,
				policyholderLocator,
				policyLocator,
				state,
			);
			return response;
		},
		{
			getNextPageParam: (lastPage) =>
				lastPage.data.pagingToken === ''
					? undefined
					: lastPage.data.pagingToken,
			keepPreviousData: true,
		},
	);
};

export const getPaymentQueryKey = (
	paymentLocator: string,
	authToken: string,
) => ['payments', paymentLocator, authToken];

export const usePayment = (
	paymentLocator: string,
	authToken: string,
	cashManagementEnabled = false,
	hasFetchPermission = false,
) => {
	return useQuery({
		queryKey: getPaymentQueryKey(paymentLocator, authToken),
		queryFn: async () => {
			const response = await PaymentsApi.getPayment(authToken, paymentLocator);
			return response.data;
		},
		enabled: !!paymentLocator && cashManagementEnabled && hasFetchPermission,
	});
};

export default PaymentsApi;
